import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  listUsers,
  searchUsers,
  changeUserRole,
  setUserActivation,
  deleteUser,
  updateAdminUserProfile,
  addUserRole,
  removeUserRole,
  AdminUser,
  UpdateAdminUserProfileRequest,
} from '@/services/api/admin';

export const ADMIN_USERS_QUERY_KEY = ['admin', 'users'];

export const useAdminUsers = () => {
  const query = useQuery({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: async () => {
      // Fetch all pages (simple approach for admin portal)
      const allUsers: AdminUser[] = [];
      let nextToken: string | undefined;

      do {
        const response = await listUsers(nextToken);
        allUsers.push(...response.users);
        nextToken = response.nextToken;
      } while (nextToken);

      return allUsers;
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    users: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Admin Users list, real single-Cognito-page-at-a-time pagination +
 * server-side search (see #1268 — replaces looping listUsers through every
 * Cognito page before rendering anything, the same anti-pattern fixed for
 * Properties in #1249/#1267). Search term is part of the query key, so
 * changing it resets pagination for free. See searchUsers/searchUsers's own
 * comments for the prefix-match-only search limitation vs the old
 * client-side substring search.
 */
export const useAdminUsersSearch = ({
  search = '',
  enabled = true,
}: { search?: string; enabled?: boolean } = {}) => {
  const query = useInfiniteQuery({
    queryKey: [...ADMIN_USERS_QUERY_KEY, 'search', search],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      search ? searchUsers(search) : listUsers(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    staleTime: 30 * 1000,
    enabled,
  });

  return {
    users: query.data?.pages.flatMap((page) => page.users) ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useChangeUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: 'Admin' | 'Advisor' | 'Buyer' }) =>
      changeUserRole(username, role),
    onMutate: async ({ username, role }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ADMIN_USERS_QUERY_KEY });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData<AdminUser[]>(ADMIN_USERS_QUERY_KEY);

      // Optimistically update to the new value
      if (previousUsers) {
        queryClient.setQueryData<AdminUser[]>(
          ADMIN_USERS_QUERY_KEY,
          (old) =>
            old?.map((user) =>
              user.username === username ? { ...user, platformRole: role } : user,
            ) ?? [],
        );
      }

      // Return a context object with the snapshotted value
      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUsers) {
        queryClient.setQueryData(ADMIN_USERS_QUERY_KEY, context.previousUsers);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};

export const useSetUserActivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, activate }: { username: string; activate: boolean }) =>
      setUserActivation(username, activate),
    onMutate: async ({ username, activate }) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      const previousUsers = queryClient.getQueryData<AdminUser[]>(ADMIN_USERS_QUERY_KEY);

      if (previousUsers) {
        queryClient.setQueryData<AdminUser[]>(
          ADMIN_USERS_QUERY_KEY,
          (old) =>
            old?.map((user) =>
              user.username === username ? { ...user, enabled: activate } : user,
            ) ?? [],
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(ADMIN_USERS_QUERY_KEY, context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username }: { username: string }) => deleteUser(username),
    onMutate: async ({ username }) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      const previousUsers = queryClient.getQueryData<AdminUser[]>(ADMIN_USERS_QUERY_KEY);

      if (previousUsers) {
        queryClient.setQueryData<AdminUser[]>(
          ADMIN_USERS_QUERY_KEY,
          (old) => old?.filter((user) => user.username !== username) ?? [],
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(ADMIN_USERS_QUERY_KEY, context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};

export const useToggleAdminAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, grant }: { username: string; grant: boolean }) =>
      grant ? addUserRole(username, 'Admin') : removeUserRole(username, 'Admin'),
    onMutate: async ({ username, grant }) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      const previousUsers = queryClient.getQueryData<AdminUser[]>(ADMIN_USERS_QUERY_KEY);

      if (previousUsers) {
        queryClient.setQueryData<AdminUser[]>(
          ADMIN_USERS_QUERY_KEY,
          (old) =>
            old?.map((user) => {
              if (user.username !== username) return user;
              const groups = grant
                ? Array.from(new Set([...user.groups, 'Admin']))
                : user.groups.filter((g) => g !== 'Admin');
              return { ...user, groups };
            }) ?? [],
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(ADMIN_USERS_QUERY_KEY, context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};

export const useUpdateAdminUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: UpdateAdminUserProfileRequest }) =>
      updateAdminUserProfile(userId, request),
    onMutate: async ({ userId, request }) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      const previousUsers = queryClient.getQueryData<AdminUser[]>(ADMIN_USERS_QUERY_KEY);

      if (previousUsers) {
        queryClient.setQueryData<AdminUser[]>(
          ADMIN_USERS_QUERY_KEY,
          (old) =>
            old?.map((user) => {
              if (user.userId !== userId) return user;

              // Build the updated profile: apply SET for non-null values,
              // apply REMOVE (delete key) for explicit nulls — matching DB PATCH semantics.
              const updatedProfile = { ...(user.profile ?? {}) };
              for (const [key, value] of Object.entries(request)) {
                if (value === undefined) continue;
                if (value === null || value === '') {
                  delete (updatedProfile as Record<string, unknown>)[key];
                } else {
                  (updatedProfile as Record<string, unknown>)[key] = value;
                }
              }

              // Optimistically update the Cognito-sourced `name` field when name changes
              const firstName = request.firstName ?? updatedProfile.firstName;
              const lastName = request.lastName ?? updatedProfile.lastName;
              const nameUpdate =
                request.firstName !== undefined || request.lastName !== undefined
                  ? { name: [firstName, lastName].filter(Boolean).join(' ') }
                  : {};

              return { ...user, ...nameUpdate, profile: updatedProfile };
            }) ?? [],
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(ADMIN_USERS_QUERY_KEY, context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
};
